import React, { useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Grid,
  Alert,
  Stepper,
  Step,
  StepLabel,
  StepContent,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControlLabel,
  Checkbox
} from '@mui/material';
import {
  Security as SecurityIcon,
  CheckCircle as CheckIcon,
  Info as InfoIcon,
  AccountBalance as BankIcon,
  VpnKey as KeyIcon,
  Shield as ShieldIcon,
  Verified as VerifiedIcon,
  CloudSync as SyncIcon
} from '@mui/icons-material';

interface OpenBankingSetupProps {
  bankAccounts: any[];
  onRefresh: () => void;
  loading: boolean;
}

const OpenBankingSetup: React.FC<OpenBankingSetupProps> = ({
  bankAccounts,
  onRefresh,
  loading
}) => {
  const [activeStep, setActiveStep] = useState(0);
  const [showPermissionsDialog, setShowPermissionsDialog] = useState(false);
  const [selectedBank, setSelectedBank] = useState<string>('');
  const [permissions, setPermissions] = useState({
    read_accounts: true,
    read_balances: true,
    read_transactions: true,
    read_direct_debits: false,
    read_standing_orders: false
  });

  const setupSteps = [
    {
      label: 'Choose Your Bank',
      description: 'Select your UK bank from our supported providers',
      completed: false
    },
    {
      label: 'Grant Permissions',
      description: 'Authorize secure access to your account data',
      completed: false
    },
    {
      label: 'Authenticate',
      description: 'Log in securely through your bank\'s website',
      completed: false
    },
    {
      label: 'Verify Connection',
      description: 'Confirm successful connection and data sync',
      completed: false
    }
  ];

  const supportedBanks = [
    { name: 'Barclays', logo: '🏦', status: 'available', features: ['Instant sync', 'Real-time balance'] },
    { name: 'HSBC', logo: '🏦', status: 'available', features: ['Instant sync', 'Transaction history'] },
    { name: 'Lloyds Bank', logo: '🏦', status: 'available', features: ['Daily sync', 'Account details'] },
    { name: 'NatWest', logo: '🏦', status: 'available', features: ['Instant sync', 'Direct debits'] },
    { name: 'Santander UK', logo: '🏦', status: 'available', features: ['Real-time balance', 'Standing orders'] },
    { name: 'TSB', logo: '🏦', status: 'available', features: ['Daily sync', 'Transaction categorization'] },
    { name: 'Nationwide', logo: '🏦', status: 'available', features: ['Instant sync', 'Savings accounts'] },
    { name: 'Halifax', logo: '🏦', status: 'available', features: ['Real-time balance', 'Credit cards'] },
    { name: 'Monzo', logo: '🏦', status: 'available', features: ['Instant sync', 'Real-time notifications'] },
    { name: 'Starling Bank', logo: '🏦', status: 'available', features: ['Instant sync', 'Business accounts'] }
  ];

  const openBankingBenefits = [
    {
      icon: <SecurityIcon color="primary" />,
      title: 'Bank-Level Security',
      description: 'Your credentials are never shared. Authentication happens directly with your bank.'
    },
    {
      icon: <SyncIcon color="success" />,
      title: 'Automatic Sync',
      description: 'Transactions are imported automatically, saving you time on manual data entry.'
    },
    {
      icon: <VerifiedIcon color="info" />,
      title: 'FCA Regulated',
      description: 'We are authorized by the Financial Conduct Authority for Open Banking services.'
    },
    {
      icon: <ShieldIcon color="warning" />,
      title: 'Data Protection',
      description: 'Your data is encrypted and stored securely in compliance with UK data protection laws.'
    }
  ];

  const handleBankSelect = (bankName: string) => {
    setSelectedBank(bankName);
    setShowPermissionsDialog(true);
  };

  const handlePermissionChange = (permission: string) => {
    setPermissions(prev => ({
      ...prev,
      [permission]: !prev[permission as keyof typeof prev]
    }));
  };

  const handleConnect = async () => {
    try {
      setShowPermissionsDialog(false);
      setActiveStep(1);

      // Convert bank name to bank_id (lowercase, no spaces)
      const bankId = selectedBank.toLowerCase().replace(/\s+/g, '');

      const response = await fetch(`/api/v1/banking/connect-bank?bank_id=${bankId}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const data = await response.json();

        if (data.success && data.authorization_url) {
          // Redirect to bank's authorization page
          window.location.href = data.authorization_url;
        } else {
          setActiveStep(0);
          alert(`Connection failed: ${data.message || 'Unknown error'}`);
        }
      } else {
        const errorData = await response.json();
        setActiveStep(0);
        alert(`Connection failed: ${errorData.detail || 'Server error'}`);
      }
    } catch (error) {
      console.error('Error connecting to bank:', error);
      setActiveStep(0);
      alert('Connection failed. Please check your internet connection and try again.');
    }
  };

  return (
    <Box>
      {/* Open Banking Overview */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Open Banking Setup
          </Typography>

          <Alert severity="info" sx={{ mb: 3 }}>
            <Typography variant="body2">
              <strong>What is Open Banking?</strong> Open Banking allows you to securely share your financial data
              with authorized third parties. This enables automatic transaction import and real-time account monitoring.
            </Typography>
          </Alert>

          <Grid container spacing={3}>
            {openBankingBenefits.map((benefit, index) => (
              <Grid item xs={12} md={6} key={index}>
                <Box display="flex" alignItems="flex-start" gap={2}>
                  {benefit.icon}
                  <Box>
                    <Typography variant="body1" fontWeight="medium" gutterBottom>
                      {benefit.title}
                    </Typography>
                    <Typography variant="body2" color="textSecondary">
                      {benefit.description}
                    </Typography>
                  </Box>
                </Box>
              </Grid>
            ))}
          </Grid>
        </CardContent>
      </Card>

      {/* Setup Progress */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Connection Setup Progress
          </Typography>

          <Stepper activeStep={activeStep} orientation="vertical">
            {setupSteps.map((step, index) => (
              <Step key={index}>
                <StepLabel>
                  {step.label}
                </StepLabel>
                <StepContent>
                  <Typography variant="body2" color="textSecondary">
                    {step.description}
                  </Typography>
                </StepContent>
              </Step>
            ))}
          </Stepper>
        </CardContent>
      </Card>

      {/* Supported Banks */}
      <Card>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Supported UK Banks
          </Typography>

          <Typography variant="body2" color="textSecondary" sx={{ mb: 3 }}>
            Choose your bank to begin the secure connection process. All major UK banks are supported.
          </Typography>

          <Grid container spacing={2}>
            {supportedBanks.map((bank, index) => (
              <Grid item xs={12} md={4} key={index}>
                <Card
                  variant="outlined"
                  sx={{
                    cursor: 'pointer',
                    '&:hover': { bgcolor: 'action.hover' },
                    height: '100%'
                  }}
                  onClick={() => handleBankSelect(bank.name)}
                >
                  <CardContent>
                    <Box display="flex" alignItems="center" gap={2} mb={2}>
                      <Typography variant="h4">
                        {bank.logo}
                      </Typography>
                      <Box>
                        <Typography variant="body1" fontWeight="medium">
                          {bank.name}
                        </Typography>
                        <Chip
                          label={bank.status}
                          color="success"
                          size="small"
                        />
                      </Box>
                    </Box>

                    <List dense>
                      {bank.features.map((feature, featureIndex) => (
                        <ListItem key={featureIndex} sx={{ px: 0 }}>
                          <ListItemIcon sx={{ minWidth: 32 }}>
                            <CheckIcon color="success" fontSize="small" />
                          </ListItemIcon>
                          <ListItemText
                            primary={feature}
                            primaryTypographyProps={{ variant: 'body2' }}
                          />
                        </ListItem>
                      ))}
                    </List>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        </CardContent>
      </Card>

      {/* Permissions Dialog */}
      <Dialog open={showPermissionsDialog} onClose={() => setShowPermissionsDialog(false)} maxWidth="md" fullWidth>
        <DialogTitle>
          <Box display="flex" alignItems="center" gap={1}>
            <KeyIcon />
            Grant Permissions - {selectedBank}
          </Box>
        </DialogTitle>
        <DialogContent>
          <Alert severity="info" sx={{ mb: 3 }}>
            <Typography variant="body2">
              <strong>Secure Authorization:</strong> You will be redirected to {selectedBank}'s secure website
              to log in and authorize these permissions. We never see your banking credentials.
            </Typography>
          </Alert>

          <Typography variant="h6" gutterBottom>
            Data Access Permissions
          </Typography>

          <Box sx={{ mb: 3 }}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={permissions.read_accounts}
                  onChange={() => handlePermissionChange('read_accounts')}
                />
              }
              label="Read account information (Required)"
            />
            <Typography variant="body2" color="textSecondary" sx={{ ml: 4, mb: 1 }}>
              Access basic account details like account number and type
            </Typography>

            <FormControlLabel
              control={
                <Checkbox
                  checked={permissions.read_balances}
                  onChange={() => handlePermissionChange('read_balances')}
                />
              }
              label="Read account balances (Required)"
            />
            <Typography variant="body2" color="textSecondary" sx={{ ml: 4, mb: 1 }}>
              View current and available balances
            </Typography>

            <FormControlLabel
              control={
                <Checkbox
                  checked={permissions.read_transactions}
                  onChange={() => handlePermissionChange('read_transactions')}
                />
              }
              label="Read transaction history (Required)"
            />
            <Typography variant="body2" color="textSecondary" sx={{ ml: 4, mb: 1 }}>
              Import transaction data for automatic categorization
            </Typography>

            <FormControlLabel
              control={
                <Checkbox
                  checked={permissions.read_direct_debits}
                  onChange={() => handlePermissionChange('read_direct_debits')}
                />
              }
              label="Read direct debits (Optional)"
            />
            <Typography variant="body2" color="textSecondary" sx={{ ml: 4, mb: 1 }}>
              View scheduled direct debit payments
            </Typography>

            <FormControlLabel
              control={
                <Checkbox
                  checked={permissions.read_standing_orders}
                  onChange={() => handlePermissionChange('read_standing_orders')}
                />
              }
              label="Read standing orders (Optional)"
            />
            <Typography variant="body2" color="textSecondary" sx={{ ml: 4 }}>
              View scheduled standing order payments
            </Typography>
          </Box>

          <Alert severity="warning">
            <Typography variant="body2">
              <strong>Data Retention:</strong> Your data will be stored securely for up to 90 days.
              You can revoke access at any time through your bank's website or our settings.
            </Typography>
          </Alert>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowPermissionsDialog(false)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleConnect}
            disabled={!permissions.read_accounts || !permissions.read_balances || !permissions.read_transactions}
          >
            Connect to {selectedBank}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default OpenBankingSetup;
